/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { cloneDeep } from "lodash-es";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Chain } from "viem/chains";
import { mainnet, useNetwork, useSwitchNetwork } from "wagmi";
import CheckIcon from "../../img/header_icons/check.svg?react";
import { GlobalStore } from "../../store/global/GlobalStore";
import { CHAINS, SUPPORTED_CHAINS } from "../../config/chains";
import { getChainFromId, isStoredNetworkOutOfSync } from "../../utils/chainUtils";
import { updateUrlParams, URL_KEYS } from "../../utils/urlUtils";
import { MODAL_SELECTORS } from "../Modal/instance/modalSelectors";
import { NetworkButton } from "./NetworkButton";
import "./NetworkDropdown.css";
import { NetworkDropdownContainer } from "./elements/NetworkDropdownContainer";
import { Text } from "../general/Text";
import { FlexBetweenContainer } from "../../containers/FlexBetweenContainer";
import { FlexContainer } from "../../containers/FlexContainer";
import LoadingEllipse from "../ButterComponents/LoadingEllipse/LoadingEllipse";
import { AnimatePresence, motion, Variants } from "framer-motion";

export interface NetworkDropdownProps {}

export const NetworkDropdown: React.FC<NetworkDropdownProps> = () => {
  const network = useNetwork();
  const theme = GlobalStore.useState(s => s.theme);
  const modalSelector = GlobalStore.useState(s => s.modalSelector);
  const storeNetwork = GlobalStore.useState(s => s.network);
  const address = GlobalStore.useState(s => s.address);
  const networkId = network.chain?.id ?? storeNetwork.id;
  const containerRef = useRef<HTMLDivElement>(null);
  const networkDropdownRef = useRef<HTMLDivElement>(null);
  const [isRequestingTokens, setIsRequestingTokens] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [chainSwitchingTo, setChainSwitchingTo] = useState<Chain | undefined>(undefined);
  const { switchNetwork } = useSwitchNetwork({
    onSuccess: () => {
      GlobalStore.update(s => {
        s.isChainSwitching = false;
        s.quoteError = undefined;
      });
      setIsDropdownOpen(false);
      setChainSwitchingTo(undefined);
    },
    onError: error => {
      console.error(error);
      GlobalStore.update(s => {
        s.isChainSwitching = false;
      });
      setIsDropdownOpen(false);
      setChainSwitchingTo(undefined);
      Sentry.captureException(`Could not switch networks`, { extra: { error } });
      updateUrlParams(URL_KEYS.NETWORK, storeNetwork.name.toLowerCase(), window.location.pathname);
    },
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const currentNetworkConfig = CHAINS[networkId];
  const networksNotSelected = SUPPORTED_CHAINS.filter(config => config.id !== networkId);
  const orderedNetworks = currentNetworkConfig
    ? [currentNetworkConfig, ...networksNotSelected]
    : [...networksNotSelected];
  const defaultConfig = CHAINS[mainnet.id];
  const config = currentNetworkConfig !== undefined ? currentNetworkConfig : defaultConfig;
  const propsToSpread = {
    id: config.id,
    networkIconColor: config.iconColor,
    icon: config.icon,
  };

  const updateChain = useCallback((network: Chain | undefined) => {
    const chainMatch = SUPPORTED_CHAINS.find(entry => entry.id === network?.id) as Chain;
    if (network && chainMatch) {
      GlobalStore.update(s => {
        s.network = cloneDeep(chainMatch) as typeof s.network;
      });
    }
    setIsRequestingTokens(false);
  }, []);

  useEffect(() => {
    // First test if the network is supported
    const isSupportedNetwork = SUPPORTED_CHAINS.map(network => network.id).includes(
      network.chain?.id ?? -1
    );
    if (network.chain && !isSupportedNetwork) {
      GlobalStore.update(s => {
        s.modalSelector = MODAL_SELECTORS.UNSUPPORTED_NETWORK;
      });
      return;
    } else {
      if (modalSelector === MODAL_SELECTORS.UNSUPPORTED_NETWORK) {
        GlobalStore.update(s => {
          s.modalSelector = undefined;
        });
      }
    }

    if (address && isStoredNetworkOutOfSync(storeNetwork, network.chain) && !isRequestingTokens) {
      setIsRequestingTokens(true);
      void updateChain(network.chain);
    }
  }, [
    address,
    isRequestingTokens,
    modalSelector,
    network,
    storeNetwork,
    storeNetwork.id,
    updateChain,
  ]);

  useEffect(() => {
    if (chainSwitchingTo !== undefined) {
      GlobalStore.update(s => {
        s.isChainSwitching = true;
      });
    }
  }, [chainSwitchingTo]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef?.current &&
        !containerRef.current.contains(event.target as Node) &&
        chainSwitchingTo === undefined
      ) {
        if (
          networkDropdownRef.current &&
          !networkDropdownRef.current.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false);
        }
      }
    },
    [containerRef, networkDropdownRef, chainSwitchingTo]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleOnClick = useCallback(
    (networkId: number) => {
      const newChain = getChainFromId(networkId);
      // First get metadata for the new chain
      if (newChain && networkId !== network?.chain?.id) {
        if (switchNetwork) {
          // Remove network from url to prevent network switch back
          updateUrlParams(
            URL_KEYS.NETWORK,
            newChain?.name.toLowerCase() ?? "",
            window.location.pathname
          );
          setChainSwitchingTo(getChainFromId(networkId));
          switchNetwork(networkId);
        } else {
          // If switchNetwork is not defined - a wallet is not connect - just update state/
          if (newChain) {
            void updateChain(newChain);
          }
        }
      }
    },
    [network?.chain?.id, switchNetwork, updateChain]
  );

  return (
    <NetworkDropdownContainer theme={theme} data-testid={"network-dropdown"} ref={containerRef}>
      <NetworkButton
        onClick={() => setIsDropdownOpen(true)}
        {...propsToSpread}
        isDropdownOpen={isDropdownOpen}
        ref={buttonRef}
      >
        <AnimatePresence>
          {isDropdownOpen && (
            <NetworkDropdownContent
              variants={slideDownVariants}
              initial={"initial"}
              animate={"slide"}
              exit={"exit"}
              buttonSize={buttonRef.current?.clientWidth}
              data-testid={"network-dropdown-dialog"}
              ref={networkDropdownRef}
            >
              {Object.values(orderedNetworks).map(props => {
                const isSelected = networkId === props.id;
                const Icon = props.icon;
                return (
                  <NetworkButtonRow
                    key={props.id}
                    isSelected={
                      (isSelected && chainSwitchingTo === undefined) ||
                      chainSwitchingTo?.id === props.id
                    }
                    data-testid={`${
                      networkId === props.id ? "selected-" : `${props.id}-`
                    }network-dropdown-option`}
                    onClick={() => !isSelected && handleOnClick(Number(props.id))}
                  >
                    <FlexBetweenContainer widthExpand={true}>
                      <FlexContainer gap={8}>
                        <Icon width={22} height={22} color={props.iconColor} />
                        <Text textStyle="Title" size="XS">
                          {props.displayName}
                        </Text>
                      </FlexContainer>
                      {isSelected && <CheckIcon stroke={theme["Colour ramps"].Green["500"]} />}
                      {chainSwitchingTo?.id === props.id && <LoadingEllipse />}
                    </FlexBetweenContainer>
                  </NetworkButtonRow>
                );
              })}
            </NetworkDropdownContent>
          )}
        </AnimatePresence>
      </NetworkButton>
    </NetworkDropdownContainer>
  );
};

// Sub-components

const slideDownVariants: Variants = {
  initial: { height: -20, opacity: 0, display: "none", transition: { duration: 0.2 } },
  slide: { height: "auto", opacity: 1, display: "flex", transition: { duration: 0.2 } },
  exit: { height: "0", opacity: 0, transition: { duration: 0.2 } },
};

const NetworkButtonRow = styled.div<{ isSelected: boolean }>`
  background: ${props => (props.isSelected ? props.theme.surface.main.secondary.hover : "none")};

  &:hover {
    background-color: ${props => props.theme.surface.alt.secondary.hover};
  }

  border-radius: 14px;
  display: flex;
  padding: 10px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

export const NetworkDropdownContent = styled(motion.div)<{
  buttonSize: number | undefined;
}>`
  flex-direction: column;
  gap: 2px;
  background-color: ${props => props.theme.surface.alt.secondary.default};
  box-shadow: ${props => props.theme.shadow.overlay};
  border-radius: 22px;
  position: absolute;
  min-width: 184px;
  padding: 8px;
  color: ${props => props.theme.font.primary.default};
  top: -14px;
  right: -13px;
  z-index: 999;
`;
